import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Download, TrendingDown } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, Legend,
} from 'recharts';
import { downloadCsv, rowsToCsv } from '@/lib/csvExport';

type ListingType = 'property' | 'plot' | 'project' | 'commercial' | 'all';

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function endOfDay(dateStr: string): string {
  const d = new Date(dateStr);
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
}

function startOfDay(dateStr: string): string {
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function bucketByDay(rows: { created_at: string }[]): Record<string, number> {
  const out: Record<string, number> = {};
  rows.forEach(r => {
    const d = new Date(r.created_at).toISOString().slice(0, 10);
    out[d] = (out[d] || 0) + 1;
  });
  return out;
}

export function FunnelDashboard() {
  const [range, setRange] = useState<'7' | '30' | '90' | 'custom'>('30');
  const [from, setFrom] = useState<string>(() => new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10));
  const [to, setTo] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [listingType, setListingType] = useState<ListingType>('all');
  const [city, setCity] = useState<string>('all');

  const fromIso = range === 'custom' ? startOfDay(from) : isoDaysAgo(parseInt(range, 10));
  const toIso = range === 'custom' ? endOfDay(to) : new Date().toISOString();

  const { data, isLoading } = useQuery({
    queryKey: ['insights-funnel', range, from, to, listingType, city],
    queryFn: async () => {
      const baseView = supabase.from('listing_views' as any)
        .select('listing_type, listing_id, city, created_at')
        .gte('created_at', fromIso).lte('created_at', toIso);
      const baseReveal = supabase.from('contact_reveals' as any)
        .select('listing_type, listing_id, city, reveal_type, created_at')
        .gte('created_at', fromIso).lte('created_at', toIso);
      const baseLead = supabase.from('leads')
        .select('id, status, created_at, property_id, plot_id, project_id')
        .gte('created_at', fromIso).lte('created_at', toIso);

      const [vResp, rResp, lResp] = await Promise.all([baseView, baseReveal, baseLead]);
      let views = (vResp.data || []) as any[];
      let reveals = (rResp.data || []) as any[];
      let leads = (lResp.data || []) as any[];

      if (listingType !== 'all') {
        views = views.filter(v => v.listing_type === listingType);
        reveals = reveals.filter(r => r.listing_type === listingType);
        leads = leads.filter(l =>
          listingType === 'property' || listingType === 'commercial' ? !!l.property_id :
          listingType === 'plot' ? !!l.plot_id :
          listingType === 'project' ? !!l.project_id : true
        );
      }
      if (city !== 'all') {
        views = views.filter(v => (v.city || '').toLowerCase() === city.toLowerCase());
        reveals = reveals.filter(r => (r.city || '').toLowerCase() === city.toLowerCase());
      }
      return { views, reveals, leads };
    },
  });

  const { data: cityOptions } = useQuery({
    queryKey: ['insights-funnel-cities'],
    queryFn: async () => {
      const { data } = await supabase.from('listing_views' as any)
        .select('city')
        .not('city', 'is', null)
        .limit(1000);
      const set = new Set<string>();
      (data || []).forEach((r: any) => r.city && set.add(r.city));
      return Array.from(set).sort();
    },
    staleTime: 5 * 60 * 1000,
  });

  const funnel = useMemo(() => {
    const v = data?.views.length ?? 0;
    const r = data?.reveals.length ?? 0;
    const l = data?.leads.length ?? 0;
    // Pipeline stages used by the leads table: new → contacted → converted (with 'closed' for dropped).
    const q = data?.leads.filter(x => x.status === 'contacted' || x.status === 'converted').length ?? 0;
    const w = data?.leads.filter(x => x.status === 'converted').length ?? 0;
    const pct = (a: number, b: number) => (b > 0 ? Math.round((a / b) * 1000) / 10 : 0);
    return {
      stages: [
        { name: 'Views', value: v },
        { name: 'Reveals', value: r },
        { name: 'Leads', value: l },
        { name: 'Qualified', value: q },
        { name: 'Won', value: w },
      ],
      rates: {
        viewToReveal: pct(r, v),
        revealToLead: pct(l, r),
        leadToQualified: pct(q, l),
        qualifiedToWon: pct(w, q),
        viewToLead: pct(l, v),
      },
    };
  }, [data]);

  const trend = useMemo(() => {
    const vMap = bucketByDay(data?.views ?? []);
    const lMap = bucketByDay(data?.leads ?? []);
    const days = new Set<string>([...Object.keys(vMap), ...Object.keys(lMap)]);
    return Array.from(days).sort().map(d => ({
      day: d,
      views: vMap[d] || 0,
      leads: lMap[d] || 0,
    }));
  }, [data]);

  const leaks = useMemo(() => {
    const byCity: Record<string, { views: number; leads: number }> = {};
    (data?.views ?? []).forEach(v => {
      const k = v.city || 'Unknown';
      byCity[k] = byCity[k] || { views: 0, leads: 0 };
      byCity[k].views += 1;
    });
    (data?.leads ?? []).forEach(_ => {
      // we don't have city on leads — skip
    });
    return Object.entries(byCity)
      .filter(([_, n]) => n.views >= 5 && n.leads === 0)
      .sort((a, b) => b[1].views - a[1].views)
      .slice(0, 8)
      .map(([city, n]) => ({ city, views: n.views, leads: n.leads }));
  }, [data]);

  const exportCsv = () => {
    const rows = funnel.stages.map(s => ({ stage: s.name, count: s.value }));
    const csv = rowsToCsv(rows);
    downloadCsv(`funnel_${range === 'custom' ? `${from}_${to}` : `last${range}d`}.csv`, csv);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Funnel Analytics</h1>
          <p className="text-sm text-muted-foreground">Views → Reveals → Leads → Qualified → Won</p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv}>
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Range</label>
            <Select value={range} onValueChange={(v: any) => setRange(v)}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {range === 'custom' && (
            <>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">From</label>
                <Input type="date" value={from} onChange={e => setFrom(e.target.value)} className="w-40" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">To</label>
                <Input type="date" value={to} onChange={e => setTo(e.target.value)} className="w-40" />
              </div>
            </>
          )}
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Listing type</label>
            <Select value={listingType} onValueChange={(v: any) => setListingType(v)}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="property">Properties</SelectItem>
                <SelectItem value="commercial">Commercial</SelectItem>
                <SelectItem value="plot">Plots</SelectItem>
                <SelectItem value="project">Projects</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">City</label>
            <Select value={city} onValueChange={setCity}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All cities</SelectItem>
                {(cityOptions || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Funnel bars */}
      {isLoading ? (
        <Skeleton className="h-72 w-full rounded-lg" />
      ) : (
        <Card>
          <CardHeader><CardTitle className="text-base">Conversion funnel</CardTitle></CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={funnel.stages} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="name" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-4">
              <RateChip label="View → Reveal" value={funnel.rates.viewToReveal} />
              <RateChip label="Reveal → Lead" value={funnel.rates.revealToLead} />
              <RateChip label="Lead → Qualified" value={funnel.rates.leadToQualified} />
              <RateChip label="Qualified → Won" value={funnel.rates.qualifiedToWon} />
              <RateChip label="View → Lead" value={funnel.rates.viewToLead} highlight />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Daily trend */}
      <Card>
        <CardHeader><CardTitle className="text-base">Views vs. leads (daily)</CardTitle></CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="views" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="leads" stroke="hsl(var(--accent-foreground))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Leaks */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-destructive" /> Top leaks (cities with views, no leads)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {leaks.length === 0 ? (
            <p className="text-sm text-muted-foreground">No significant leaks detected in this range.</p>
          ) : (
            <div className="space-y-2">
              {leaks.map(l => (
                <div key={l.city} className="flex items-center justify-between p-2 border rounded-md">
                  <div className="font-medium text-sm">{l.city}</div>
                  <div className="text-xs text-muted-foreground">
                    {l.views} views · <span className="text-destructive font-semibold">0 leads</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function RateChip({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div className={`p-3 rounded-lg border ${highlight ? 'bg-primary/5 border-primary/30' : 'bg-card'}`}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-xl font-bold mt-1">{value}%</div>
    </div>
  );
}
